import { useBrandPlan } from 'hooks/queries/brandPlans/useBrandPlan'
import Styles from './BrandPlanCurrentVolume.module.scss'
import { BrandPlanCurrentVolumeContainerProps, BrandPlanCurrentVolumeProps } from './Types'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { EMAIL_TACTICS, SEO_TACTICS, SOCIAL_MEDIA_TACTICS } from '../BrandPlanTactics/BrandPlanTactics'
import { Formik } from 'formik'
import { number, object } from 'yup'
import { useMemo, useState } from 'react'
import { Social } from './Social'
import { Seo } from './Seo'
import { Email } from './Email'
import { useUpdateBrandPlan, Variables } from 'hooks/queries/useUpdateBrandPlan'
import { BrandPlanTactic } from 'types'
import { generatePath, useHistory } from 'react-router-dom'
import { SCHEDULED_POSTS_PATH } from 'paths'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { useOnboard } from 'hooks/queries/useOnboard'
import dayjs from 'dayjs'
import { WorkspaceClient } from 'api/clients/WorkspaceClient'
import store from 'redux/store'
import { useWorkspaces } from 'hooks/useWorkspaces'
import { workspacesSet } from 'redux/actions'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useStartBrandPlan } from 'hooks/queries/brandPlans/useStartBrandPlan'
import { useOnboardingTracking } from 'hooks/useOnboardingTracking'
import { Form } from 'components/FirstTimeExperienceModal/components/Form/Form'
import { FooterButtons, StickyFooter } from 'components/FirstTimeExperienceModal/components/Footer/Footer'
import { Button } from 'blaze-ui/Button'
import { ArrowLeft, ArrowRight } from 'blaze-ui/icons'
import VirtualMarketerOnboardingStyles from 'screens/VirtualMarketerOnboarding/VirtualMarketerOnboarding.module.scss'
import { StickyFooterProvider } from 'providers/StickyFooterProvider/StickyFooterProvider'
import { IntercomHelp } from 'flows/common/components/IntercomHelp/IntercomHelp'
import { Explanation } from 'flows/common/components/Explanation/Explanation'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { ProgressLoader } from 'components/IntegrationsBanner/ProgressLoader/ProgressLoader'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { Text } from 'blaze-ui/Text/Text'
import { sendMessageToNativeApp } from 'utils/sendMessageToNativeApp'
import { useExperiment } from 'utils/experiments'

const BrandPlanCurrentVolume = ({ brandPlan, onBack, onNext, selectedBlogPlatform }: BrandPlanCurrentVolumeProps) => {
  const { isMobile, isIOSNative } = useResponsiveDesign()
  const [isDirty, setIsDirty] = useState(false)
  const { mutate: updateBrandPlan } = useUpdateBrandPlan({ id: brandPlan.id })
  const tactics = brandPlan.tactics
  const history = useHistory()
  const { mutateAsync: onboard } = useOnboard()
  const { mutateAsync: startBrandPlan, isLoading: isGenerating } = useStartBrandPlan({ brandPlanId: brandPlan.id })
  const currentWorkspaceId = useCurrentWorkspaceId()
  const workspaces = useWorkspaces()
  const { trackGenerateContentClicked } = useOnboardingTracking()
  const { data: integrations } = useExternalIntegrations()
  const mobileCalendarAccessVariant = useExperiment('mobile_calendar_access')

  const markVirtualMarketerOnboardingCompleted = async () => {
    await WorkspaceClient.completeVirtualMarketerOnboarding(currentWorkspaceId)
    store.dispatch(
      workspacesSet(
        workspaces.map((ws) =>
          ws.id === currentWorkspaceId ? { ...ws, is_virtual_marketer_onboarding_completed: true } : ws,
        ),
      ),
    )
  }

  const schema = useMemo(() => {
    let schema = object({})

    if (tactics.includes(BrandPlanTactic.Email)) {
      schema = schema.concat(
        object({
          email_avg_open_rate: number().required(''),
          email_avg_click_through_rate: number().required(''),
        }),
      )
    }

    return schema
  }, [tactics])

  const handleSubmit = async (values: Variables) => {
    // Track the generate content button click
    trackGenerateContentClicked()

    updateBrandPlan(values, {
      onSuccess: async () => {
        try {
          const { first_schedule: schedule } = await startBrandPlan({})
          await onboard()
          await markVirtualMarketerOnboardingCompleted()

          const pathname = generatePath(SCHEDULED_POSTS_PATH, {
            workspaceId: brandPlan.workspace_id,
          })
          if (schedule?.id) {
            const search = `?date=${dayjs(schedule.start_date).format('YYYY-MM-DD')}&load-tutorial=true`
            if (isIOSNative) {
              sendMessageToNativeApp('didFinishOnboarding')
              return
            }
            // A/B test: block/allow mobile calendar
            if (isMobile && mobileCalendarAccessVariant !== 'allow') {
              onNext({
                desktopPath: `${pathname}${search}`,
                publishDay: dayjs(schedule.start_date).format('dddd'),
              })
            } else {
              history.replace({ pathname, search })
            }
          } else {
            showErrorMessageToast('Failed to generate content, please try again.')
          }
        } catch (error) {
          showErrorMessageToast('Failed to generate content, please try again.')
        }
      },
    })
  }

  const numConnectedAccounts = integrations?.external_integrations?.length || 0
  let numPossibleConnections = tactics.length
  if (tactics.includes(BrandPlanTactic.SEO)) {
    numPossibleConnections += 1
  }

  const percentage = (numConnectedAccounts / numPossibleConnections) * 100

  return (
    <Formik
      validationSchema={schema}
      validateOnChange={isDirty}
      validateOnBlur={isDirty}
      initialValues={schema.cast(
        {
          ...schema.getDefault(),
          facebook_avg_daily_impressions: brandPlan.facebook_avg_daily_impressions,
          instagram_avg_daily_impressions: brandPlan.instagram_avg_daily_impressions,
          linkedin_avg_daily_impressions: brandPlan.linkedin_avg_daily_impressions,
          twitter_avg_daily_impressions: brandPlan.twitter_avg_daily_impressions,
          email_avg_open_rate: !brandPlan.email_avg_open_rate ? 0 : brandPlan.email_avg_open_rate,
          email_avg_click_through_rate: !brandPlan.email_avg_click_through_rate
            ? 0
            : brandPlan.email_avg_click_through_rate,
          seo_avg_daily_visits: brandPlan.seo_avg_daily_visits,
        },
        { assert: false },
      )}
      onSubmit={handleSubmit}
    >
      {({ submitForm, validateForm }) => (
        <Form>
          <MultiStepStepLayout
            title="Connect your accounts to activate autopilot"
            scrollContainerClassName={VirtualMarketerOnboardingStyles.scrollContainer}
            contentClassName={VirtualMarketerOnboardingStyles.content}
          >
            <StickyFooterProvider
              footer={
                <StickyFooter>
                  <FooterButtons>
                    <ResponsiveDesign.Desktop>
                      <Button variant="tertiary" size="xl" onPress={onBack} frontIcon={ArrowLeft} aria-label="Back">
                        Back
                      </Button>
                    </ResponsiveDesign.Desktop>
                    <Button
                      size="xl"
                      onPress={() => {
                        setIsDirty(true)
                        validateForm()
                        submitForm()
                      }}
                      endIcon={ArrowRight}
                      aria-label="Generate Content"
                      isDisabled={isGenerating}
                      fullWidth={isMobile}
                    >
                      {isGenerating ? 'Generating...' : 'Generate Content'}
                    </Button>
                  </FooterButtons>
                </StickyFooter>
              }
            >
              <div className={Styles.brandPlanCurrentVolume}>
                <ResponsiveDesign.Desktop>
                  <div className={Styles.banners}>
                    <Explanation variant="secondary">
                      Blaze will automatically post your content and learn what works best. Review or edit anything
                      before it goes live.
                    </Explanation>
                    <IntercomHelp />
                  </div>
                </ResponsiveDesign.Desktop>
                <ResponsiveDesign.Mobile>
                  <div className={Styles.progressContainer}>
                    <ProgressLoader percentage={percentage} size={30} strokeWidth={4} />
                    <Text>
                      {numConnectedAccounts}/{numPossibleConnections} accounts connected!
                    </Text>
                  </div>
                </ResponsiveDesign.Mobile>

                {SOCIAL_MEDIA_TACTICS.some((tactic) => tactics.includes(tactic)) && (
                  <Social tactics={tactics} size={isMobile ? 'sm' : 'md'} />
                )}
                {SEO_TACTICS.some((tactic) => tactics.includes(tactic)) && (
                  <Seo selectedBlogPlatform={selectedBlogPlatform} size={isMobile ? 'sm' : 'md'} />
                )}
                {EMAIL_TACTICS.some((tactic) => tactics.includes(tactic)) && <Email size={isMobile ? 'sm' : 'md'} />}
              </div>
            </StickyFooterProvider>
          </MultiStepStepLayout>
        </Form>
      )}
    </Formik>
  )
}

const BrandPlanCurrentVolumeContainer = ({
  id,
  onBack,
  onNext,
  selectedBlogPlatform,
  selectedEmailPlatform,
}: BrandPlanCurrentVolumeContainerProps) => {
  const { data: brandPlan } = useBrandPlan(id)

  if (!brandPlan) {
    return null
  }

  return (
    <BrandPlanCurrentVolume
      brandPlan={brandPlan}
      onBack={onBack}
      onNext={onNext}
      selectedBlogPlatform={selectedBlogPlatform}
    />
  )
}

export { BrandPlanCurrentVolumeContainer as BrandPlanCurrentVolume }
